import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getMyChatUnreadTotal, subscribeToMyChatInboxV2 } from '../api/chatApiV2';
import { fetchMyAppUserRow } from '../api/profileApi';
import { CHAT_UNREAD_REFRESH } from '../lib/chatEvents';
import { isSupabaseConfigured } from '../lib/supabase';
import { useMingo } from './MingoContext';

const ChatInboxContext = createContext(null);

async function noopRefresh() {}

export function ChatInboxProvider({ children }) {
  const { isLoggedIn, isGuest } = useMingo();
  const [userId, setUserId] = useState(null);
  const [tick, setTick] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => {
    if (!isLoggedIn || isGuest) {
      setUserId(null);
      return undefined;
    }
    let cancelled = false;
    void fetchMyAppUserRow().then((me) => {
      if (!cancelled) setUserId(me?.id || null);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, isGuest]);

  const refreshUnread = useCallback(async () => {
    if (!userId) {
      setUnreadTotal(0);
      return;
    }
    try {
      const total = await getMyChatUnreadTotal();
      setUnreadTotal(total);
    } catch {
      setUnreadTotal(0);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !isSupabaseConfigured) {
      setUnreadTotal(0);
      setTick(0);
      return undefined;
    }

    void refreshUnread();

    let timer;
    const onInboxChange = () => {
      setTick((t) => t + 1);
      clearTimeout(timer);
      timer = window.setTimeout(() => void refreshUnread(), 320);
    };

    const unsub = subscribeToMyChatInboxV2(userId, onInboxChange);
    return () => {
      clearTimeout(timer);
      unsub();
    };
  }, [userId, refreshUnread]);

  useEffect(() => {
    const onRefresh = () => void refreshUnread();
    window.addEventListener(CHAT_UNREAD_REFRESH, onRefresh);
    return () => window.removeEventListener(CHAT_UNREAD_REFRESH, onRefresh);
  }, [refreshUnread]);

  return (
    <ChatInboxContext.Provider value={{ tick, unreadTotal, refreshUnread }}>
      {children}
    </ChatInboxContext.Provider>
  );
}

export function useChatInbox() {
  const ctx = useContext(ChatInboxContext);
  if (!ctx) {
    return { tick: 0, unreadTotal: 0, refreshUnread: noopRefresh };
  }
  return ctx;
}
